// AndroidStripeRedirect — paywall replacement shown INSTEAD of
// NativePaywall when the app is running inside the Capacitor Android
// shell. Google Play IAP is paused while the tax form is pending, so
// Android users complete payment through our Stripe checkout
// **embedded inside the WebView** (route `/checkout`) — no Chrome
// Custom Tab, no system browser, no deep-link return. The N10 promo
// code is auto-applied (10% off) as the Android-exclusive hook since
// Stripe does not offer the Apple 3-day free trial.
//
// Flow (fully inside the app):
//   1. User lands here after signup / inside SubscriptionGate.
//   2. Big "Subscribe" button → `navigate("/checkout?coupon=N10")`.
//   3. Checkout.jsx renders Stripe `PaymentElement` natively inside
//      the WebView — looks like a Calofay-branded screen, not Chrome.
//   4. Stripe `confirmPayment` returns to /payment-success which
//      flips `subscription_status` to "active" and bounces to /app.

import { Ticket, Sparkles, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n.jsx";
import { isNativeApp, nativePlatform, appPromoCode } from "@/lib/native";

const COPY = {
  ar: {
    kicker: "عرض محدود للمشتركين الجدد 🎟️",
    headline: "كود خصم لمدة محدودة للمشتركين الجدد",
    sub: "فعّله في الصفحة التالية — راح يتفعّل تلقائيًا عند الاشتراك.",
    codeLabel: "كود الخصم",
    bullets: [
      "خصم 10٪ فوري على الاشتراك",
      "دفع آمن عبر بوابة Stripe داخل التطبيق",
      "ضمان استرجاع كامل خلال 15 يوم",
    ],
    cta: "اذهب إلى صفحة الاشتراك",
    fineprint: "صفحة الدفع تفتح داخل التطبيق — الكود يُطبّق تلقائيًا.",
    secureNote: "دفع آمن عبر Stripe",
  },
  en: {
    kicker: "Limited offer for new subscribers 🎟️",
    headline: "A limited-time discount code for new subscribers",
    sub: "Activate it on the next page — it applies automatically at checkout.",
    codeLabel: "Discount code",
    bullets: [
      "Instant 10% off your subscription",
      "Secure in-app checkout via Stripe",
      "Full 15-day money-back guarantee",
    ],
    cta: "Continue to subscription",
    fineprint: "Checkout opens inside the app — the code applies automatically.",
    secureNote: "Secure payment via Stripe",
  },
};

export default function AndroidStripeRedirect() {
  const nav = useNavigate();
  const { lang } = useI18n();
  const isRTL = lang === "ar";
  const t = COPY[lang] || COPY.ar;

  const handleContinue = () => {
    const code = appPromoCode();
    try { localStorage.setItem("calofay_affiliate_code", code); } catch (_) {}
    // iOS/GCC branch: tag the checkout URL with `src=ios_gcc` so
    // Checkout.jsx (a) permits iOS native through, (b) auto-creates
    // the Stripe session the moment the plan is selected, and (c)
    // renders Apple Pay as the ONLY payment surface — card entry
    // hidden, no "Continue to payment" intermediate button. Default
    // plan preselected via `plan=annual` so the user's single tap
    // on the Apple Pay button fires the sheet directly.
    const isIos = isNativeApp() && nativePlatform() === "ios";
    const route = isIos
      ? `/checkout?coupon=${code}&skip_lead=1&src=ios_gcc&plan=annual`
      : `/checkout?coupon=${code}&skip_lead=1`;
    nav(route);
  };

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="w-full"
      data-testid="android-stripe-redirect"
    >
      <style>{`
        @keyframes astrPromoFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-5px); }
        }
      `}</style>

      <div className="flex flex-col items-center">
        {/* Guarantee moved to its own dedicated onboarding screen
            (`OnboardingGuaranteeScreen`) that is shown BEFORE the promo
            page in `IOSOnboarding.jsx`. Owner request — restore the
            paywall-return view to its pre-guarantee single-focus
            state (code → CTA) and show the guarantee only once, up
            front, on its own screen. */}

        <div
          className="relative w-32 h-32 rounded-full flex items-center justify-center mt-2"
          style={{
            background: "linear-gradient(145deg, #FFE08A 0%, #F5A623 55%, #C77800 100%)",
            boxShadow:
              "0 22px 48px -12px rgba(199,120,0,0.55), inset 0 -6px 12px rgba(0,0,0,0.15), inset 0 6px 12px rgba(255,255,255,0.5)",
            animation: "astrPromoFloat 4s ease-in-out infinite",
          }}
        >
          <Ticket className="w-16 h-16 text-white drop-shadow-lg" strokeWidth={2.2} />
          <Sparkles className="absolute -top-2 -right-2 w-5 h-5 text-yellow-200 fill-yellow-100 drop-shadow-lg" />
        </div>

        <span className="inline-flex items-center gap-1.5 bg-brand-500 text-white text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-sm mt-4">
          <Sparkles className="w-3.5 h-3.5 fill-white" strokeWidth={2.5} />
          {t.kicker}
        </span>

        <h1 className="text-2xl font-black text-neutral-900 text-center leading-tight mt-3 px-2">
          {t.headline}
        </h1>

        <p className="text-sm text-neutral-600 text-center mt-3 leading-relaxed px-3 max-w-sm">
          {t.sub}
        </p>

        <div className="mt-6 w-full max-w-xs mx-auto">
          <div className="bg-white rounded-2xl border-2 border-dashed border-brand-400 p-4 text-center shadow-sm">
            <div className="text-[10px] font-bold text-brand-600 uppercase tracking-widest mb-1.5">
              {t.codeLabel}
            </div>
            <div
              className="text-3xl font-black tracking-[0.3em] text-neutral-900 num"
              dir="ltr"
              data-testid="android-redirect-code"
            >
              {appPromoCode()}
            </div>
          </div>
        </div>

        <ul className="mt-6 space-y-3 text-[14.5px] text-neutral-700 w-full max-w-sm px-2">
          {t.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-500 text-white text-[11px] font-black flex items-center justify-center flex-shrink-0">
                ✓
              </span>
              <span className="leading-[1.7]">{b}</span>
            </li>
          ))}
        </ul>

        <div className="w-full max-w-sm mt-7 px-2">
          <button
            type="button"
            onClick={handleContinue}
            className="w-full orange-gradient text-white font-black py-4 rounded-full shadow-floating active:scale-[0.98] transition-transform text-base flex items-center justify-center gap-2"
            data-testid="android-redirect-cta"
          >
            {t.cta}
            {isRTL ? <ArrowLeft className="w-5 h-5" /> : <ArrowLeft className="w-5 h-5 rotate-180" />}
          </button>
          {/* "Secure payment via Stripe" + "Checkout opens inside the
              app — code applies automatically" fineprints removed per
              owner request — redundant with the ticket card above and
              lengthened the screen without adding information. */}
        </div>
      </div>
    </div>
  );
}
